// src/pages/product/DataSources/AddDataSource/StepOne.jsx
import React, { useState, useEffect, useRef, useContext } from 'react';
import './CreateDataSource.css';
import { UserContext, WorkspaceContext, SetupTrackerContext, OrgContext } from '../../objects/Context';
import postgresLogo from '../../assets/images/integrations/postgres.png';
import excelLogo from '../../assets/images/integrations/excel.png';
import quickbooksLogo from '../../assets/images/integrations/quickbooks.png';
import salesforceLogo from '../../assets/images/integrations/salesforce.png';
import gustoLogo from '../../assets/images/integrations/gusto.png';
import bambooHRLogo from '../../assets/images/integrations/bambooHR.png';
import ripplingLogo from '../../assets/images/integrations/rippling.png';
import sageIntacctLogo from '../../assets/images/integrations/sage-intacct.png';
import waveLogo from '../../assets/images/integrations/wave.png';
import xeroLogo from '../../assets/images/integrations/xero.png';
import {ReactComponent as DownIcon} from '../../assets/icons/down-icon.svg'
import {ReactComponent as SearchIcon} from '../../assets/icons/search-icon.svg'
import Quickbooks from './SourceAccess/Quickbooks';
import Excel from './SourceAccess/Excel';
import axios from 'axios';
import LoadingScreen from '../../components/LoadingScreen/LoadingScreen';

function CreateDataSource({setAddDataSourceOpen, setDataSources, setAlert, initialStep, initialAuthStatus, initialDataSource, initialDataSourceType, setShowUsagePopup}) {
    const { user } = useContext(UserContext);
    const { workspace } = useContext(WorkspaceContext);
    const { org } = useContext(OrgContext);
    const { setupSteps, setSetupSteps } = useContext(SetupTrackerContext);
    const [newDataSource, setNewDataSource] = useState(initialDataSource || null);
    const [dataSourceType, setDataSourceType] = useState(initialDataSourceType || '');
    const [accessType, setAccessType] = useState('team-wide');
    const [step, setStep] = useState(initialStep || 1);
    const inputRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    // integration
    const [authStatus, setAuthStatus] = useState(initialAuthStatus || null);
    const [file, setFile] = useState(null);
    // source selection
    const [sourceSelectionOpen, setSourceSelectionOpen] = useState(false);
    // image rendering for selection
    const [imagesLoaded, setImagesLoaded] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    // schema selection
    const [schema, setSchema] = useState([]);
    const [expandedTables, setExpandedTables] = useState({});
    const [selectedSchema, setSelectedSchema] = useState([]);
    // Add state for file upload errors
    const [uploadError, setUploadError] = useState(null);


    //////////////////// data sources ////////////////////
    const dataSources = [
        // { name: 'Excel', logo: excelLogo },
        { name: 'QuickBooks', logo: quickbooksLogo }
    ];

    const comingSoonDataSources = [
        { name: 'Xero', logo: xeroLogo },
        { name: 'Salesforce', logo: salesforceLogo },
        { name: 'Gusto', logo: gustoLogo },
        { name: 'BambooHR', logo: bambooHRLogo },
        { name: 'Rippling', logo: ripplingLogo },
        { name: 'Sage Intacct', logo: sageIntacctLogo },
        { name: 'Wave', logo: waveLogo },
    ];

    const filteredDataSources = dataSources.filter(source =>
        source.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredComingSoonDataSources = comingSoonDataSources.filter(source =>
        source.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    //////////////////// use effect ////////////////////


    useEffect(() => {
        const imageUrls = [...dataSources, ...comingSoonDataSources]
            .map(source => source.logo)
            .filter(Boolean);
        let loadedImages = 0;
        const totalImages = imageUrls.length;
        const handleImageLoad = () => {
            loadedImages++;
            if (loadedImages === totalImages) {
                setImagesLoaded(true);
            }
        };
        imageUrls.forEach(url => {
            const img = new Image();
            img.src = url;
            img.onload = handleImageLoad;
            img.onerror = handleImageLoad;
        });
    }, []);

    useEffect(() => {
        // Only run if there's a newDataSource
        if (newDataSource) {
            const updateDataSources = (prevDataSources) => {
                const existingIndex = prevDataSources.findIndex(ds => ds.id === newDataSource.id);
                if (existingIndex !== -1) {
                    // Update existing data source
                    const updatedDataSources = [...prevDataSources];
                    updatedDataSources[existingIndex] = newDataSource;
                    return updatedDataSources;
                } else {
                    // Add new data source
                    return [...prevDataSources, newDataSource];
                }
            };
            setDataSources(updateDataSources);
        }
    }, [newDataSource]);

    // useEffect(() => {
    //     if (step === 3 && schema.length === 0) {
    //         getSchema();
    //     }
    // }, [schema]);

    //////////////////// database functions ////////////////////
    const createDataSource = async () => {
        setLoading(true);
        setErrorMessage('');
        if (inputRef.current.value && accessType && dataSourceType && workspace.id) {
            try {
                const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/data-sources/create-data-source`, {
                    creatorUserId: user.id,
                    name: inputRef.current.value,
                    accessType,
                    workspaceId: workspace.id,
                    status: 'disconnected',
                    accessType: accessType,
                    syncFreq: 'daily',
                    dataSourceType: dataSourceType
                }, { withCredentials: true });
                setStep(2);
                setNewDataSource(response.data.dataSource);
            } catch (error) {
                console.error('Error creating data source:', error);

                if (error.response.data.error === 'You already have this data source.' || error.response.status === 409) {
                    setAlert('This workspace already has a QuickBooks!', 'You can only have one data source of each type per workspace. To add a new QuickBooks, please create a new workspace or delete the existing QuickBooks data source.', false, 10000);
                } else if (error.response.data.error_code === 'limit-exceeded') {
                    setShowUsagePopup(true);
                } else {
                    setErrorMessage(error.response.data.error);
                }
            } finally {
                setLoading(false);
            }
        } else {
            setErrorMessage('Please fill out all fields');
            setLoading(false);
        }
    };

    const connectDataSource = async () => {
        setLoading(true);
        setErrorMessage('');
        setUploadError(null); // Clear any previous upload errors
        
        setNewDataSource((prevDataSource) => ({
            ...prevDataSource,
            status: 'syncing'
        }));
        const formDataToSend = new FormData();
        if (file && file instanceof File) {
            formDataToSend.append('file', file);
        } else {
            console.log('No valid file selected.');
        }
        formDataToSend.append('creatorUserId', user.id);
        formDataToSend.append('workspaceId', workspace.id);
        formDataToSend.append('vectorStoreId', workspace.vectorStoreId);
        formDataToSend.append('data', JSON.stringify(newDataSource));
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/data-sources/connect`, 
                formDataToSend, { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true });
            if (dataSourceType === 'Excel') {
                setAlert("Successfully connected data source!", "You can now begin using this data source in Vairo.", true);
                setNewDataSource((prevDataSource) => ({
                    ...prevDataSource,
                    status: 'ready',
                    lastSynced: new Date()
                }));
                setTimeout(() => {
                    setAddDataSourceOpen(false);
                }, 100);
            } else {
                setAlert("Sync process started!", "You can begin using this data source once the sync is complete.", true);
                setTimeout(() => {
                    setAddDataSourceOpen(false);
                }, 100);
            }
            updateSetupProgress();
        } catch (error) {
            setNewDataSource((prevDataSource) => ({
                ...prevDataSource,
                status: 'sync-failed'
            }));
            
            // Handle Excel validation errors (status 400)
            if (error.response && error.response.status === 400 && dataSourceType === 'Excel') {
                // Extract error message from the response
                const errorMsg = error.response.data.error || 'The Excel file does not meet the required format.';
                setUploadError(errorMsg);
            } else {
                setErrorMessage('Error making connection.');
            }
            
            console.error('Error making connection:', error);
            if (error.response) {
                console.error('Error details:', error.response.data);
            }
        } finally {
            setLoading(false);
        }
    };

    // const syncDataSource = async () => {
    //     setLoading(true);
    //     setErrorMessage('');
    //     if (selectedSchema.length === 0) {
    //         setErrorMessage('Please select at least one table to sync.');
    //         setLoading(false);
    //         return;
    //     }
    //     try {
    //         const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/data-sources/sync`, {
    //             id: newDataSource.id,
    //             dataSourceType: dataSourceType,
    //             selectedSchema: selectedSchema,
    //             workspaceId: workspace.id
    //         }, { withCredentials: true });
    //         setAlert("Sync process started!", "You can begin using this data source once the sync is complete.", true);

    //         setTimeout(() => {
    //             setAddDataSourceOpen(false);
    //         }, 100);
    //     } catch (error) {
    //         console.error('Error syncing data source:', error);
    //         setErrorMessage('Error syncing data source.');
    //         if (error.response) {
    //             console.error('Error details:', error.response.data);
    //         }
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const disconnectDataSource = async () => {
        setLoading(true);
        setErrorMessage('');
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/data-sources/disconnect`, {
                dataSourceId: newDataSource.id,
                dataSourceType: dataSourceType,
                workspaceId: workspace.id,
                vectorStoreId: workspace.vectorStoreId
            }, { withCredentials: true });
            setAuthStatus(null);
            setNewDataSource((prevDataSource) => ({
                ...prevDataSource,
                status: 'disconnected',
                lastSynced: null
            }));
        } catch (error) {
            console.error('Error disconnecting data source:', error);
        } finally {
            setLoading(false);
        }
    };

    const updateSetupProgress = async () => {
        // try {
        //     const stepId = 2;
        //     if (org?.showSetup && !setupSteps.find(step => step.id === stepId).completed) {
        //         await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/orgs/update-setup-progress`, {
        //             orgId: org.id,
        //             figureToUpdate: 'connectSource',
        //             completed: true
        //         }, { withCredentials: true });
        //         setSetupSteps(prevSteps => prevSteps.map(step => 
        //             step.id === stepId ? { ...step, completed: true } : step
        //         ));
        //     }
        // } catch (error) {
        //     console.error('Error updating setup progress:', error);
        // }
    };

    // const getSchema = async () => {
    //     try {
    //         setLoading(true);
    //         const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/data-sources/get-schema/${newDataSource.id}`, { withCredentials: true });
    //         setSchema(response.data.schema);
    //     } catch (error) {
    //         console.error('Error getting schema:', error);
    //         setErrorMessage('Error getting schema.');
    //     } finally {
    //         setLoading(false);
    //     }
    // };
    


    //////////////////// handler functions ////////////////////
    const handleOptionClick = (dataSource) => {
        setDataSourceType(dataSource);
        setSourceSelectionOpen(false);
    };

    //////////////////// render functions ////////////////////

    const renderStepOne = () => (
        <div className="step one">
            <h2>New Data Source</h2>
            <div className="add-item-inputs">
                <input type="text" placeholder="Connection Name" ref={inputRef} />
                <div className="select-source-wrapper" onClick={() => setSourceSelectionOpen(!sourceSelectionOpen)}>
                    {dataSourceType ? (
                        <div className="selected-source">
                            <img src={dataSources.find(source => source.name === dataSourceType).logo} alt={"selected data source"} />
                            <h4>{dataSourceType}</h4>
                        </div>
                    ) : (
                        <h4>Select a data source</h4>
                    )}
                    <DownIcon className={`down-icon ${sourceSelectionOpen ? 'open' : ''}`} />
                    {sourceSelectionOpen && renderSelection()}
                </div>
                {/* <div className="access-type-wrapper">
                    <div className="text">
                        <h4>{accessType === 'team-wide' ? 'Access - Team wide' : 'Access - Private'}</h4>
                        <p>{accessType === 'team-wide' ? 'Visible to your workspace team-members' : 'Visible on an invite-only basis'}</p>
                    </div>
                    <div className={`toggle ${accessType === 'team-wide' ? 'team-wide' : 'private'}`} onClick={() => setAccessType(accessType === 'team-wide' ? 'private' : 'team-wide')}>
                        <div className='slider'></div>
                    </div>
                </div> */}
                
            </div>
            <div className="add-item-actions">
                <button className="add-item-button cancel" onClick={() => setAddDataSourceOpen(false)}>Cancel</button>
                <button className="add-item-button create" onClick={() => createDataSource()}>Continue</button>
            </div>
        </div>
    );

    const renderStepTwo = () => {
        switch (dataSourceType) {
            case 'QuickBooks':
                return <Quickbooks dataSourceId={newDataSource.id} authStatus={authStatus} setNewDataSource={setNewDataSource} setAuthStatus={setAuthStatus} setAddDataSourceOpen={setAddDataSourceOpen} connectDataSource={connectDataSource} disconnectDataSource={disconnectDataSource} />;
            case 'Excel':
                return <Excel 
                    setFile={setFile} 
                    setAddDataSourceOpen={setAddDataSourceOpen} 
                    connectDataSource={connectDataSource}
                    uploadError={uploadError}
                    setUploadError={setUploadError}
                />;
            default:
                return null;
        }
    };

    // const renderStepThree = () => {
    //     // Transform the schema data to the expected format
    //     const transformedSchema = schema.map(table => ({
    //         name: table.streamName,
    //         columns: table.propertyFields.map(field => 
    //             Array.isArray(field) ? field.join(', ') : field
    //         )
    //     }));

    //     return (
    //         <div className="step three">
    //             <h2>Select Data to Sync</h2>
    //             <div className="add-item-inputs schema-selection">
    //                 <div className="schema-selection-wrapper">
    //                     <div className="table-header select-all-header">
    //                         <input type="checkbox" className="table-checkbox"
    //                             checked={selectedSchema.length === transformedSchema.length && 
    //                                 transformedSchema.every(table => 
    //                                     selectedSchema.find(t => t.name === table.name)?.columns.length === table.columns.length
    //                                 )}
    //                             onChange={() => {
    //                                 setSelectedSchema(prev => {
    //                                     if (prev.length === transformedSchema.length) {
    //                                         return [];
    //                                     } else {
    //                                         return transformedSchema.map(table => ({
    //                                             name: table.name,
    //                                             columns: [...table.columns]
    //                                         }));
    //                                     }
    //                                 });
    //                             }}
    //                         />
    //                         <span className="table-name">Select All Tables</span>
    //                     </div>
    //                     {transformedSchema?.map((table) => {
    //                         const isTableSelected = selectedSchema.some(t => t.name === table.name);
    //                         const selectedTable = selectedSchema.find(t => t.name === table.name);
    //                         const allColumnsSelected = isTableSelected && 
    //                             selectedTable.columns.length === table.columns.length;
                            
    //                         return (
    //                             <div key={table.name} className="table-item">
    //                                 <div className="table-header">
    //                                     <input
    //                                         type="checkbox"
    //                                         className="table-checkbox"
    //                                         checked={allColumnsSelected}
    //                                         onChange={() => {
    //                                             setSelectedSchema(prev => {
    //                                                 if (allColumnsSelected) {
    //                                                     return prev.filter(t => t.name !== table.name);
    //                                                 } else {
    //                                                     const withoutTable = prev.filter(t => t.name !== table.name);
    //                                                     return [...withoutTable, {
    //                                                         name: table.name,
    //                                                         columns: [...table.columns]
    //                                                     }];
    //                                                 }
    //                                             });
    //                                         }}
    //                                     />
    //                                     <span className="table-name">{table.name}</span>
    //                                     <DownIcon 
    //                                         className={`down-icon ${expandedTables[table.name] ? 'open' : ''}`}
    //                                         onClick={() => setExpandedTables(prev => ({
    //                                             ...prev,
    //                                             [table.name]: !prev[table.name]
    //                                         }))}
    //                                     />
    //                                 </div>
    //                                 {expandedTables[table.name] && (
    //                                     <div className="column-list">
    //                                         {table.columns?.map((column) => (
    //                                             <div key={column} className="column-item">
    //                                                 <input
    //                                                     type="checkbox"
    //                                                     checked={selectedTable?.columns.includes(column) || false}
    //                                                     onChange={() => {
    //                                                         setSelectedSchema(prev => {
    //                                                             if (!isTableSelected) {
    //                                                                 return [...prev, {
    //                                                                     name: table.name,
    //                                                                     columns: [column]
    //                                                                 }];
    //                                                             }
    //                                                             return prev.map(t => {
    //                                                                 if (t.name !== table.name) return t;
                                                                    
    //                                                                 let newColumns;
    //                                                                 if (t.columns.includes(column)) {
    //                                                                     newColumns = t.columns.filter(c => c !== column);
    //                                                                 } else {
    //                                                                     newColumns = [...t.columns, column];
    //                                                                 }
    //                                                                 if (newColumns.length === 0) {
    //                                                                     return null;
    //                                                                 }
    //                                                                 return { ...t, columns: newColumns };
    //                                                             }).filter(Boolean); // Remove null entries
    //                                                         });
    //                                                     }}
    //                                                 />
    //                                                 <span className="column-name">{column}</span>
    //                                             </div>
    //                                         ))}
    //                                     </div>
    //                                 )}
    //                             </div>
    //                         );
    //                     })}
    //                 </div>
    //             </div>
    //             <div className="add-item-actions">
    //                 <button className="add-item-button cancel" onClick={() => setAddDataSourceOpen(false)}>Cancel</button>
    //                 <button className="add-item-button create" onClick={() => syncDataSource()}>Sync Data</button>
    //             </div>
    //         </div>
    //     );
    // };

    const renderSelection = () => (
        <div className="source-selection" onClick={(e) => e.stopPropagation()}>
            {!imagesLoaded ? (
                <div className="loading-screen-wrapper">
                    <LoadingScreen />
                </div>
            ) : (
                <>
                    <div className="search-wrapper">
                        <SearchIcon className="search-icon" />
                        <input
                            type="text"
                            className="source-search-bar"
                            placeholder="Search data sources..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className="datasource-options">
                        {filteredDataSources.map((source) => (
                            <div 
                                key={source.name}
                                className={`datasource-option ${dataSourceType === source.name ? 'selected' : ''}`} 
                                onClick={() => handleOptionClick(source.name)}
                            >
                                {source.logo && source.name !== 'Request Data Source' ? (
                                    <img src={source.logo} alt={source.name} />
                                ) : (
                                    source.icon
                                )}
                                <h4>{source.name}</h4>
                            </div>
                        ))}
                    </div>
                    <h3 className="coming-soon-title">Coming Soon!</h3>
                    <div className="datasource-options">
                        {filteredComingSoonDataSources.map((source) => (
                            <div key={source.name} className={`datasource-option`} >
                                {source.logo && source.name !== 'Request Data Source' ? (
                                    <img src={source.logo} alt={source.name} />
                                ) : (
                                    source.icon
                                )}
                                <h4>{source.name}</h4>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );

    return (
        <div className={`add-item-wrapper create-source ${sourceSelectionOpen ? 'on' : 'off'}`}>
            {loading && <LoadingScreen />}
            {step === 1 ? (
                renderStepOne()
            ) : (
                renderStepTwo()
            )}
            {errorMessage && <h3 className="error-message">{errorMessage}</h3>}
        </div>
    );
}

export default CreateDataSource;